import type { GraphEdge, GraphKind, GraphNode, GraphRel } from '@/lib/apiTypes'

/**
 * Pure view logic for the dependency graph: filtering, lineage isolation, blast radius and
 * cycle detection. Kept out of the components so it can be tested without a DOM — the app
 * has no component tests and vitest only picks up `src/**\/*.test.ts`.
 *
 * Edges point CONSUMER → DEPENDENCY throughout. "Upstream" therefore means the things that
 * depend on a node (its blast radius) and "downstream" means the things it depends on (its
 * bill of materials). Getting this backwards is the easiest mistake to make here, so every
 * function below says which direction it walks.
 */

export type Direction = 'up' | 'down' | 'both'
export type Depth = 1 | 2 | 'all'

// ── Filters ───────────────────────────────────────────────────────────────────

export type RiskFilter = 'blocked' | 'deprecated' | 'high_risk' | 'policy_failed' | 'outdated'
export type OwnershipFilter = 'internal' | 'external'

export interface FilterState {
  /** Empty means "every kind" — an explicit empty set would show nothing, which no one wants. */
  kinds: Set<GraphKind>
  risk: Set<RiskFilter>
  ownership: Set<OwnershipFilter>
  /** Provider types (`aws`, `google`, …). Empty means no provider-type narrowing. */
  providers: Set<string>
  /**
   * Provider namespaces / vendors (`hashicorp`, `integrations`, …). Narrows the same dimension
   * as `providers`, one level up: a provider node carries its vendor directly, and a module's
   * bare provider type is attributed to a vendor through the graph's own provider nodes (see
   * `vendorsOf`). A module node's `namespace` is its ORG slug, which is an entirely different
   * thing and is never read as a vendor.
   */
  vendors: Set<string>
  hideOrphans: boolean
  search: string
}

export const EMPTY_FILTERS: FilterState = {
  kinds: new Set(),
  risk: new Set(),
  ownership: new Set(),
  providers: new Set(),
  vendors: new Set(),
  hideOrphans: false,
  search: '',
}

export const ALL_KINDS: GraphKind[] = ['state', 'module', 'provider']
export const ALL_RISKS: RiskFilter[] = ['blocked', 'deprecated', 'high_risk', 'policy_failed', 'outdated']

export function matchesRisk(n: GraphNode, f: RiskFilter): boolean {
  switch (f) {
    case 'blocked': return n.blocked
    case 'deprecated': return n.deprecated
    case 'high_risk': return n.critical_count > 0 || n.high_count > 0
    case 'policy_failed': return n.policy_failed
    case 'outdated': return n.outdated
  }
}

/**
 * The provider type a node is associated with, for the provider-type filter.
 *
 * A module's is the bare third segment of its registry address (`acme/vpc/aws` -> `aws`), which
 * carries NO namespace — which is why the provider facet is keyed on the type and not on
 * `namespace/type`. Keying it on the full address would mean no module could ever match it.
 */
export function providerTypeOf(n: GraphNode): string | null {
  if (n.kind === 'provider') return n.provider_type ?? null
  if (n.kind === 'module') return n.module_provider ?? null
  return null
}

/** The vendor a provider node was published under. Only a provider node carries one directly. */
export function vendorOf(n: GraphNode): string | null {
  return n.kind === 'provider' ? (n.namespace || null) : null
}

/** Provider type -> the namespaces that publish it, read off the graph's own provider nodes. */
export type VendorIndex = Map<string, Set<string>>

export function buildVendorIndex(nodes: GraphNode[]): VendorIndex {
  const index: VendorIndex = new Map()
  for (const n of nodes) {
    if (n.kind !== 'provider') continue
    const t = n.provider_type
    if (!t || !n.namespace) continue
    const set = index.get(t) ?? new Set<string>()
    set.add(n.namespace)
    index.set(t, set)
  }
  return index
}

/**
 * Every vendor a node could have come from, or null when the dimension does not apply to it.
 *
 * A provider node answers for itself. A MODULE only knows a bare provider type (`aws`), so its
 * vendor is resolved through the index: the namespaces that type is published under in this
 * graph. Without that resolution a vendor selection narrowed the providers and left every
 * module standing - so picking `datadog` kept the whole aws estate on the canvas, and the
 * provider-type chips stayed lit on their module counts alone, which is the bug this fixes.
 *
 * A type published under more than one namespace matches if ANY of them is selected: the module
 * does not say which registry resolved it, so excluding it would be a guess in the strict
 * direction. A type no provider node declares resolves to null and is left alone, same as a
 * state file - the graph has nothing to attribute it to.
 */
export function vendorsOf(n: GraphNode, index?: VendorIndex): Set<string> | null {
  const own = vendorOf(n)
  if (own !== null) return new Set([own])
  if (n.kind === 'module' && index) {
    const t = providerTypeOf(n)
    const vendors = t === null ? undefined : index.get(t)
    if (vendors && vendors.size > 0) return vendors
  }
  return null
}

/**
 * OR within a group, AND across groups.
 *
 * The page used to AND every active chip together, so selecting Blocked and Deprecated asked
 * for nodes that were both — which is almost never the question. Within a group the chips are
 * alternatives; across groups they narrow.
 *
 * `search` is NOT a filter here. It highlights, so you can see where a match sits in the
 * structure rather than having the structure collapse around it.
 */
export function passesFilters(
  n: GraphNode,
  f: FilterState,
  degree: number,
  vendorIndex?: VendorIndex,
): boolean {
  if (f.kinds.size > 0 && !f.kinds.has(n.kind)) return false

  if (f.risk.size > 0) {
    let any = false
    for (const r of f.risk) if (matchesRisk(n, r)) { any = true; break }
    if (!any) return false
  }

  if (f.ownership.size > 0) {
    const own: OwnershipFilter = n.external ? 'external' : 'internal'
    if (!f.ownership.has(own)) return false
  }

  // Provider and vendor are DIMENSIONAL facets: they exclude a node only when that node has a
  // value for the dimension and it does not match. A node with no value is left alone rather
  // than excluded — a state file has no provider type, so a provider-type filter says nothing
  // about it, and dropping it would also drop the state→provider edges that make the filter
  // worth looking at. One rule, applied to both — the only difference is that a module's vendor
  // has to be resolved from its type (vendorsOf) instead of read off the node.
  if (f.providers.size > 0) {
    const t = providerTypeOf(n)
    if (t !== null && !f.providers.has(t)) return false
  }

  if (f.vendors.size > 0) {
    const vendors = vendorsOf(n, vendorIndex)
    if (vendors !== null) {
      let any = false
      for (const v of vendors) if (f.vendors.has(v)) { any = true; break }
      if (!any) return false
    }
  }

  if (f.hideOrphans && degree === 0) return false

  return true
}

/** Count of edges touching each node, in either direction. Drives "hide orphans". */
export function degreeMap(nodes: GraphNode[], edges: GraphEdge[]): Map<string, number> {
  const deg = new Map<string, number>()
  for (const n of nodes) deg.set(n.id, 0)
  for (const e of edges) {
    if (deg.has(e.from)) deg.set(e.from, (deg.get(e.from) ?? 0) + 1)
    if (deg.has(e.to)) deg.set(e.to, (deg.get(e.to) ?? 0) + 1)
  }
  return deg
}

// ── Lineage ───────────────────────────────────────────────────────────────────

interface Adjacency {
  /** id -> things it depends on (follow for downstream). */
  children: Map<string, Set<string>>
  /** id -> things that depend on it (follow for upstream). */
  parents: Map<string, Set<string>>
}

export function buildAdjacency(nodes: GraphNode[], edges: GraphEdge[]): Adjacency {
  const children = new Map<string, Set<string>>()
  const parents = new Map<string, Set<string>>()
  for (const n of nodes) {
    children.set(n.id, new Set())
    parents.set(n.id, new Set())
  }
  for (const e of edges) {
    children.get(e.from)?.add(e.to)
    parents.get(e.to)?.add(e.from)
  }
  return { children, parents }
}

/**
 * Breadth-first closure from `seeds` along one adjacency map, bounded by `depth`.
 *
 * Visited-set guarded, so a dependency cycle terminates instead of looping — which matters,
 * because cycles exist in real registries and the previous implementation dropped them from
 * the graph entirely rather than drawing them.
 */
function closure(
  seeds: Iterable<string>,
  adj: Map<string, Set<string>>,
  depth: Depth,
): Set<string> {
  const result = new Set<string>(seeds)
  let frontier = new Set<string>(seeds)
  let hops = 0
  const limit = depth === 'all' ? Infinity : depth
  while (frontier.size > 0 && hops < limit) {
    const next = new Set<string>()
    for (const id of frontier) {
      for (const nb of adj.get(id) ?? []) {
        if (!result.has(nb)) {
          result.add(nb)
          next.add(nb)
        }
      }
    }
    frontier = next
    hops += 1
  }
  return result
}

/** Everything that (transitively) depends on `seeds`. The blast radius. */
export function upstreamOf(seeds: Iterable<string>, adj: Adjacency, depth: Depth = 'all'): Set<string> {
  return closure(seeds, adj.parents, depth)
}

/** Everything `seeds` (transitively) depends on. The bill of materials. */
export function downstreamOf(seeds: Iterable<string>, adj: Adjacency, depth: Depth = 'all'): Set<string> {
  return closure(seeds, adj.children, depth)
}

/**
 * Which direction to isolate in by default, given what was clicked.
 *
 * A state file and a provider sit at opposite ends of the dependency chain, so each has one
 * interesting direction. A module sits in the middle, where one direction alone is half the
 * story — downstream is what it pulls in, upstream is who breaks when it changes — so it gets
 * both. The UI exposes an override; this is only the default.
 */
export function defaultDirection(kind: GraphKind): Direction {
  switch (kind) {
    case 'state': return 'down'
    case 'provider': return 'up'
    case 'module': return 'both'
  }
}

/** The node ids making up a node's lineage, in the given direction and depth. */
export function lineageOf(
  rootId: string,
  adj: Adjacency,
  direction: Direction,
  depth: Depth = 'all',
): Set<string> {
  if (direction === 'up') return upstreamOf([rootId], adj, depth)
  if (direction === 'down') return downstreamOf([rootId], adj, depth)
  const both = upstreamOf([rootId], adj, depth)
  for (const id of downstreamOf([rootId], adj, depth)) both.add(id)
  return both
}

// ── Blast radius ──────────────────────────────────────────────────────────────

export interface BlastRadius {
  total: number
  byKind: Record<GraphKind, number>
}

/**
 * How many things depend on each node, transitively, broken down by kind.
 *
 * Computed for every node up front so the size encoding and the "N state files downstream"
 * line in the detail panel come from one walk. Without this a 40-consumer hub and a leaf
 * render identically, which wastes the most informative channel the graph has.
 */
export function blastRadiusMap(
  nodes: GraphNode[],
  edges: GraphEdge[],
): Map<string, BlastRadius> {
  const adj = buildAdjacency(nodes, edges)
  const kindOf = new Map(nodes.map((n) => [n.id, n.kind]))
  const out = new Map<string, BlastRadius>()
  for (const n of nodes) {
    const up = upstreamOf([n.id], adj)
    up.delete(n.id)
    const byKind: Record<GraphKind, number> = { state: 0, module: 0, provider: 0 }
    for (const id of up) {
      const k = kindOf.get(id)
      if (k) byKind[k] += 1
    }
    out.set(n.id, { total: up.size, byKind })
  }
  return out
}

// ── Cycles ────────────────────────────────────────────────────────────────────

/**
 * Node ids that sit on a dependency cycle.
 *
 * Iterative Tarjan-style SCC (explicit stack, not recursion — a deep chain would otherwise
 * risk the call stack in a browser). Any strongly-connected component bigger than one node is
 * a cycle; so is a single node with an edge to itself. Circular module dependencies are a real
 * governance finding, and the previous implementation hid them by only walking forward from
 * parentless roots — a cycle has no parentless member, so it vanished.
 */
export function findCycleNodes(nodes: GraphNode[], edges: GraphEdge[]): Set<string> {
  const { children } = buildAdjacency(nodes, edges)
  const index = new Map<string, number>()
  const low = new Map<string, number>()
  const onStack = new Set<string>()
  const stack: string[] = []
  const cycles = new Set<string>()
  let counter = 0

  for (const start of nodes.map((n) => n.id)) {
    if (index.has(start)) continue
    // Each frame holds a node and an iterator position over its children.
    const work: Array<{ id: string; kids: string[]; i: number }> = [
      { id: start, kids: [...(children.get(start) ?? [])], i: 0 },
    ]
    index.set(start, counter); low.set(start, counter); counter += 1
    stack.push(start); onStack.add(start)

    while (work.length > 0) {
      const frame = work[work.length - 1]!
      if (frame.i < frame.kids.length) {
        const kid = frame.kids[frame.i]!
        frame.i += 1
        if (kid === frame.id) { cycles.add(kid); continue } // self-edge
        if (!index.has(kid)) {
          index.set(kid, counter); low.set(kid, counter); counter += 1
          stack.push(kid); onStack.add(kid)
          work.push({ id: kid, kids: [...(children.get(kid) ?? [])], i: 0 })
        } else if (onStack.has(kid)) {
          low.set(frame.id, Math.min(low.get(frame.id)!, index.get(kid)!))
        }
      } else {
        work.pop()
        const parent = work[work.length - 1]
        if (parent) low.set(parent.id, Math.min(low.get(parent.id)!, low.get(frame.id)!))
        if (low.get(frame.id) === index.get(frame.id)) {
          const component: string[] = []
          for (;;) {
            const popped = stack.pop()!
            onStack.delete(popped)
            component.push(popped)
            if (popped === frame.id) break
          }
          if (component.length > 1) for (const id of component) cycles.add(id)
        }
      }
    }
  }
  return cycles
}

// ── Edge bundles ──────────────────────────────────────────────────────────────

/** One drawn line, and the one-or-more relationships it stands for. */
export interface EdgeBundle {
  /** `from->to:rel`. Byte-identical to the id the renderer always used, now unique by build. */
  key: string
  from: string
  to: string
  rel: GraphRel
  /** At least one, in server order. */
  edges: GraphEdge[]
}

/**
 * Collapse parallel relationships into one line each.
 *
 * Two edges can share a (from, to, rel) triple for real reasons, not as a data defect:
 *
 *  - a workspace pinning one provider from BOTH registries. Provider nodes collapse
 *    terraform.io and opentofu.org deliberately, so two honest lock rows land on one node.
 *  - `module_dependencies` carries no uniqueness constraint, so a module declaring two calls
 *    to the same source with different constraints produces two rows.
 *
 * The renderer gave every edge the id `from->to:rel`, so both cases were already producing
 * duplicate React keys. Grouping fixes that and gives the detail modal somewhere to show both.
 *
 * `upstream` is deliberately NOT part of the key. Splitting the registry case into two lines
 * between the same pair of nodes would draw them on top of each other and read as a rendering
 * fault; one line that opens onto both pins is the honest picture of a node that is itself a
 * collapse. Insertion order is preserved, and the server orders the lock rows, so the bundles
 * and their contents are stable across refetches.
 */
export function bundleEdges(edges: GraphEdge[]): EdgeBundle[] {
  const out: EdgeBundle[] = []
  const byKey = new Map<string, EdgeBundle>()
  for (const e of edges) {
    const key = `${e.from}->${e.to}:${e.rel}`
    const existing = byKey.get(key)
    if (existing) {
      existing.edges.push(e)
      continue
    }
    const bundle: EdgeBundle = { key, from: e.from, to: e.to, rel: e.rel, edges: [e] }
    byKey.set(key, bundle)
    out.push(bundle)
  }
  return out
}

// ── Composition ───────────────────────────────────────────────────────────────

export interface ViewInput {
  nodes: GraphNode[]
  edges: GraphEdge[]
  filters: FilterState
  focusId: string | null
  direction: Direction
  depth: Depth
  hiddenIds: Set<string>
}

export interface ViewResult {
  nodes: GraphNode[]
  edges: GraphEdge[]
  /** Ids matching the search term, for highlighting. Undefined when not searching. */
  highlightIds: Set<string> | undefined
  /** Visible nodes with no visible edge, so the UI can corral them. */
  orphanIds: Set<string>
  cycleIds: Set<string>
  blastRadius: Map<string, BlastRadius>
  /** Nodes in the unfiltered graph, for the "N of M shown" readout. */
  totalNodes: number
}

/**
 * The single place the view is narrowed. Isolation and filters compose as one intersection:
 *
 *   visible = lineage(focus, direction, depth) ∩ filters ∩ ¬hidden
 *
 * Doing it in one pass is what lets click-to-isolate REMOVE nodes rather than dim them. The
 * old implementation had three overlapping dimming channels resolved by a nested ternary, and
 * dumped unreachable nodes in a diagonal pile on the canvas.
 */
export function computeView(input: ViewInput): ViewResult {
  const { nodes, edges, filters, focusId, direction, depth, hiddenIds } = input

  const blastRadius = blastRadiusMap(nodes, edges)
  const cycleIds = findCycleNodes(nodes, edges)
  // Built from the FULL graph, not the narrowed one: which vendors publish a type is a property
  // of the estate, so it must not shift as the view narrows (and a vendor selection would
  // otherwise erase the very provider nodes the resolution reads from).
  const vendorIndex = buildVendorIndex(nodes)

  let candidateIds: Set<string> | null = null
  if (focusId && nodes.some((n) => n.id === focusId)) {
    candidateIds = lineageOf(focusId, buildAdjacency(nodes, edges), direction, depth)
  }

  const inLineage = (id: string) => candidateIds === null || candidateIds.has(id)

  // Degree is measured on the lineage subgraph, not the whole graph: inside an isolated view
  // "orphan" has to mean "unconnected HERE", or hiding orphans would hide the root itself.
  const scopedNodes = nodes.filter((n) => inLineage(n.id) && !hiddenIds.has(n.id))
  const scopedIds = new Set(scopedNodes.map((n) => n.id))
  const scopedEdges = edges.filter((e) => scopedIds.has(e.from) && scopedIds.has(e.to))
  const deg = degreeMap(scopedNodes, scopedEdges)

  // The focused node is exempt from hideOrphans. That toggle exists for readability, and
  // erasing the very node you asked to look at is never the readable outcome - a state file
  // with no lock file has no edges at all, and it is precisely the one you want to inspect.
  // It is NOT exempt from the other groups: narrowing to kind=state while focused on a module
  // is a semantic request, and should be honoured.
  const visibleNodes = scopedNodes.filter((n) => {
    const orphanExempt = n.id === focusId
    const f = orphanExempt ? { ...filters, hideOrphans: false } : filters
    return passesFilters(n, f, deg.get(n.id) ?? 0, vendorIndex)
  })
  const visibleIds = new Set(visibleNodes.map((n) => n.id))
  const visibleEdges = scopedEdges.filter((e) => visibleIds.has(e.from) && visibleIds.has(e.to))

  const visibleDeg = degreeMap(visibleNodes, visibleEdges)
  const orphanIds = new Set(
    visibleNodes.filter((n) => (visibleDeg.get(n.id) ?? 0) === 0).map((n) => n.id),
  )

  const q = filters.search.trim().toLowerCase()
  let highlightIds: Set<string> | undefined
  if (q) {
    highlightIds = new Set(
      visibleNodes
        .filter((n) =>
          n.label.toLowerCase().includes(q)
          || n.namespace.toLowerCase().includes(q)
          || (n.module_name ?? '').toLowerCase().includes(q)
          || (n.provider_type ?? '').toLowerCase().includes(q)
          || (n.workspace_name ?? '').toLowerCase().includes(q))
        .map((n) => n.id),
    )
  }

  return {
    nodes: visibleNodes,
    edges: visibleEdges,
    highlightIds,
    orphanIds,
    cycleIds,
    blastRadius,
    totalNodes: nodes.length,
  }
}

/**
 * Per-control match counts, cross-filtered against the OTHER groups.
 *
 * A count of zero therefore means "nothing in this bucket under the current narrowing", which
 * is why the UI disables that control: clicking it could only empty the canvas. Same rule
 * SegmentedFilter documents for the providers page.
 */
export function filterCounts(
  nodes: GraphNode[],
  edges: GraphEdge[],
  filters: FilterState,
): {
  kinds: Record<GraphKind, number>
  risk: Record<RiskFilter, number>
  ownership: Record<OwnershipFilter, number>
  providers: Map<string, number>
  /** namespace -> count, with the namespaces each one covers for label disambiguation. */
  vendors: Map<string, number>
  /** Provider type -> the namespaces it appears under. A type with more than one is ambiguous. */
  namespacesByType: Map<string, Set<string>>
} {
  const deg = degreeMap(nodes, edges)
  // Which namespaces publish each provider type. Built from the UNFILTERED graph: whether a
  // type is ambiguous is a property of the org's estate, not of the current narrowing, and a
  // label that appeared and disappeared as you ticked other boxes would be worse than one that
  // never did. The counts below resolve module vendors through this same index, so a chip's
  // count and the canvas can never disagree.
  const namespacesByType = buildVendorIndex(nodes)
  const passes = (n: GraphNode, f: FilterState): boolean =>
    passesFilters(n, f, deg.get(n.id) ?? 0, namespacesByType)
  const without = <K extends keyof FilterState>(key: K): FilterState =>
    ({ ...filters, [key]: key === 'hideOrphans' ? false : new Set() }) as FilterState

  const kinds = { state: 0, module: 0, provider: 0 } as Record<GraphKind, number>
  const kindBase = without('kinds')
  for (const n of nodes) {
    if (passes(n, kindBase)) kinds[n.kind] += 1
  }

  const risk = {} as Record<RiskFilter, number>
  const riskBase = without('risk')
  for (const r of ALL_RISKS) {
    risk[r] = nodes.filter((n) => passes(n, riskBase) && matchesRisk(n, r)).length
  }

  const ownership = { internal: 0, external: 0 } as Record<OwnershipFilter, number>
  const ownBase = without('ownership')
  for (const n of nodes) {
    if (!passes(n, ownBase)) continue
    ownership[n.external ? 'external' : 'internal'] += 1
  }

  // Seeded with every provider type present in the graph, so a type that the other groups
  // currently exclude reports 0 rather than disappearing. The rail then disables it like any
  // other zero-count control instead of dropping the whole group and shifting the layout.
  const providers = new Map<string, number>()
  for (const n of nodes) {
    const t = providerTypeOf(n)
    if (t) providers.set(t, 0)
  }
  const provBase = without('providers')
  for (const n of nodes) {
    const t = providerTypeOf(n)
    if (!t) continue
    if (!passes(n, provBase)) continue
    providers.set(t, (providers.get(t) ?? 0) + 1)
  }

  // Seeded from the PROVIDER nodes, so the rail only ever offers a vendor the estate actually
  // holds an artefact from. The count then covers everything the dimension applies to, modules
  // included - it has to match what the filter does, or the chip would promise 11 and remove 22.
  // A module on an ambiguous type counts toward each of its possible vendors, the same "any
  // match" rule the filter uses.
  const vendors = new Map<string, number>()
  for (const n of nodes) {
    const v = vendorOf(n)
    if (v) vendors.set(v, 0)
  }
  const vendBase = without('vendors')
  for (const n of nodes) {
    const resolved = vendorsOf(n, namespacesByType)
    if (resolved === null) continue
    if (!passes(n, vendBase)) continue
    for (const v of resolved) if (vendors.has(v)) vendors.set(v, (vendors.get(v) ?? 0) + 1)
  }

  // `namespacesByType` is the vendor index built at the top: the rail uses it to show a vendor
  // inline only where the type is ambiguous, which is the same question the filter asks.
  return { kinds, risk, ownership, providers, vendors, namespacesByType }
}
